import { ApplicationFieldRow } from './applicationDisplay';
import { applicationFieldRows, formatApplicationSummary } from './extractPuppyApplication';
import { abbreviateLastName } from './personName';

export type ApplicationDraft = {
    ownerName: string;
    email: string;
    phone: string;
    createdAt?: string;
    sourceLabel: string;
    summary: string;
    fields: ApplicationFieldRow[];
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SKIP_KEY =
    /veterinar|if yes, who|signature|signed document|lgtbq|lgbtq|animal cruelty|draw the image|captcha|human|street address|addr_line|address line|postal|zip code|^country$|address$/i;

const FIELD_ALIASES: Record<string, string[]> = {
    firstName: ['first name', 'firstname'],
    lastName: ['last name', 'lastname'],
    email: ['e-mail', 'email'],
    phone: ['phone number', 'phonenumber', 'phone'],
    city: ['city'],
    state: ['state / province', 'state', 'province'],
    color1: ['first color preference', 'firstcolorpreference'],
    gender1: ['first gender preference', 'firstgenderpreference'],
    color2: ['second color preference', 'secondcolorpreference'],
    gender2: ['second gender preference', 'secondgenderpreference'],
    flexPrefs: ['willing to take a puppy that does not match'],
    olderDog: ['open to an older puppy or adult'],
    why: ['why are you interested'],
    firstDog: ['is this your first dog'],
    firstPuppy: ['first time raising a puppy'],
    otherPetsYes: ['do you have other pets'],
    otherPets: ['list species breed and age', 'enter n/a if none'],
    grooming: ['plans for grooming'],
    housingType: ['type of housing'],
    ownRent: ['do you:'],
    housingOther: ['if other, please explain'],
    landlord: ['landlord allow pets'],
    yard: ['fenced yard'],
    fence: ['how high is the fence'],
    adultsAgree: ['adults in the home in agreement'],
    ages: ['age of all adults and children'],
    allergies: ['allergies to dogs'],
    whereTime: ['spend most of its time'],
    training: ['how will the puppy be trained'],
    crate: ['willing to crate train'],
    indoor: ['inside or outside dog'],
    hoursAlone: ['hours will the puppy', 'left alone'],
    gift: ['surprise or gift'],
    howSoon: ['how soon do you want to bring a puppy home'],
    pickupAge: ['pick the dog up at this age'],
    austin: ['pick up your puppy in person in austin'],
    commitment: ['committed to unconditionally'],
    followUp: ['follow-up reports and photos'],
    rehome: ['contact us first in the event'],
    otherInfo: ['any other relevant information'],
    heard: ['how did you hear about us'],
    questions: ['other questions for us'],
};

const normalizeKey = (key: string): string =>
    String(key)
        .replace(/^q\d+_/, '')
        .replace(/[_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

const cleanValue = (value: string): string =>
    String(value || '')
        .replace(/\s+/g, ' ')
        .replace(/^["']+|["']+$/g, '')
        .trim();

const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        return formatPhone(digits.slice(1));
    }
    if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return cleanValue(value);
};

const looksLikeDomain = (value: string): boolean =>
    /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(value);

const joinEmailParts = (local: string, domain: string): string => {
    if (local && domain && looksLikeDomain(domain) && !local.includes('@')) {
        return `${local}@${domain}`;
    }
    return '';
};

const flattenValue = (value: unknown): string => {
    if (value == null) {
        return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return cleanValue(String(value));
    }
    if (Array.isArray(value)) {
        return value.map((item) => flattenValue(item)).filter(Boolean).join(', ');
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const username = flattenValue(record.username || record.email);
        const domain = flattenValue(record.domain);
        const fromUsername = joinEmailParts(username, domain);
        if (fromUsername) {
            return fromUsername;
        }
        if (record.first || record.last) {
            const first = flattenValue(record.first);
            const last = flattenValue(record.last);
            return joinEmailParts(first, last) || [first, last].filter(Boolean).join(' ');
        }
        if (record.full) {
            return flattenValue(record.full);
        }
        if (record.phone || record.area) {
            return flattenValue(record.full || `${record.area || ''} ${record.phone || ''}`);
        }
        return Object.values(record)
            .map((item) => flattenValue(item))
            .filter(Boolean)
            .join(' ');
    }
    return '';
};

const looksLikeAddressObject = (value: unknown): value is Record<string, unknown> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const keys = Object.keys(value).map((key) => key.toLowerCase());
    const hasCity = keys.includes('city');
    const hasState = keys.some((key) => key === 'state' || key.includes('province'));
    const hasStreet = keys.some((key) => /addr|street|postal|zip/.test(key));
    return (hasCity && hasState) || (hasStreet && (hasCity || hasState));
};

const addressPart = (record: Record<string, unknown>, match: RegExp): string => {
    for (const [key, value] of Object.entries(record)) {
        if (match.test(key)) {
            return flattenValue(value);
        }
    }
    return '';
};

export const flattenJotformRecord = (value: unknown): Record<string, string> => {
    const flat: Record<string, string> = {};
    if (!value || typeof value !== 'object') {
        return flat;
    }
    Object.entries(value as Record<string, unknown>).forEach(([key, nested]) => {
        if (key === 'pretty' && nested && typeof nested === 'object') {
            Object.assign(flat, flattenJotformRecord(nested));
            return;
        }
        if (looksLikeAddressObject(nested)) {
            const city = addressPart(nested, /^city$/i);
            const state = addressPart(nested, /^(state|province)$/i);
            if (city) {
                flat.city = city;
            }
            if (state) {
                flat.state = state;
            }
            return;
        }
        const text = flattenValue(nested);
        if (text) {
            flat[key] = text;
        }
    });
    return flat;
};

const pickField = (record: Record<string, string>, aliases: string[]): string => {
    const entries = Object.entries(record);
    for (const [key, value] of entries) {
        if (SKIP_KEY.test(key)) {
            continue;
        }
        const normalized = normalizeKey(key);
        if (aliases.some((alias) => normalized === alias || normalized.endsWith(alias))) {
            return cleanValue(value);
        }
        if (aliases.some((alias) => alias.length > 12 && normalized.includes(alias))) {
            return cleanValue(value);
        }
    }
    return '';
};

const collectPlans = (record: Record<string, string>): string => {
    const yes = Object.entries(record)
        .filter(([key, value]) => {
            const normalized = normalizeKey(key);
            if (!normalized.includes('what do you plan on doing')) {
                return false;
            }
            return /^(yes|true|1)$/i.test(cleanValue(value));
        })
        .map(([key]) => {
            const label = key.split('>>').pop() || key;
            return cleanValue(label);
        })
        .filter(Boolean);
    return yes.join(', ');
};

const parseSubmittedAt = (value: string): string | undefined => {
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
        return undefined;
    }
    return new Date(parsed).toISOString();
};

export const applicationDraftFromRecord = (
    raw: Record<string, unknown>,
    sourceLabel: string
): ApplicationDraft | null => {
    const record = flattenJotformRecord(raw);
    const fields: Record<string, string> = {};
    Object.entries(FIELD_ALIASES).forEach(([id, aliases]) => {
        const value = pickField(record, aliases);
        if (value) {
            fields[id] = id === 'phone' ? formatPhone(value) : value;
        }
    });
    const plans = collectPlans(record);
    if (plans) {
        fields.plans = plans;
    }

    const email = (fields.email || pickField(record, ['email', 'e-mail']))
        .trim()
        .toLowerCase()
        // URL-encoded bodies turn a plus-alias into a space: rachel+1@… → rachel 1@…
        .replace(/^([^\s@]+)\s+(\d+@)/, '$1+$2');
    if (!EMAIL_PATTERN.test(email)) {
        return null;
    }

    const ownerName = abbreviateLastName(
        [fields.firstName, fields.lastName].filter(Boolean).join(' ') ||
            pickField(record, ['name'])
    );
    if (!ownerName) {
        return null;
    }

    const submitted =
        pickField(record, ['submission date', 'created at', 'created_at']) ||
        String(raw['Submission Date'] || '') ||
        '';
    const applicationId =
        String(raw['Application ID'] || raw.applicationID || '') ||
        pickField(record, ['application id', 'submission id', 'submissionid']);

    return {
        ownerName,
        email,
        phone: fields.phone || '',
        createdAt: parseSubmittedAt(submitted),
        sourceLabel: applicationId ? `${sourceLabel} #${applicationId}` : sourceLabel,
        summary: formatApplicationSummary(fields),
        fields: applicationFieldRows(fields),
    };
};
